/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	Component,
	DestroyRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	signal,
	SimpleChanges,
	TemplateRef
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ECharts, EChartsOption, MapSeriesOption } from "echarts";
import { MapChart } from "../../models/http/charts/map-chart/map-chart.model";
import { ChartPattern } from "../../models/ui/charts.model";
import { WorldMapService } from "../../services/world-map.service";
import { DEFAULT_CHART_OPTIONS } from "../../utils/echarts/chart-defaults";
import { ButtonComponent } from "../button/button.component";
import { ChartComponent } from "../chart/chart.component";

@Component({
	selector: "globalictcc-map",
	standalone: true,
	imports: [ChartComponent, ButtonComponent],
	templateUrl: "./map.component.html",
	styleUrl: "./map.component.scss"
})
export class MapComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public data?: MapChart;
	@Input() public configOverrides: EChartsOption = {};
	@Input() public hasError?: boolean = false;
	@Input() public isLoading: boolean = false;
	@Input() public isGradient: boolean = false;
	@Input() public patterns: ChartPattern[] = [];
	@Input() public colors: string[] = [];
	@Input() public isTitleVisible: boolean = false;
	@Input() public isLegendVisible: boolean = false;
	@Input() public titleAlignment: "left" | "center" | "right" = "left";
	@Input() public titleSize: "small" | "large" = "large";
	@Input() public tooltip: string | TemplateRef<unknown> = "";
	@Input() public showPointPopupOnClick: boolean = false;

	@Output() public handleMapRefresh = new EventEmitter<void>();
	@Output() public handleChartInit = new EventEmitter<ECharts>();
	@Output() public handleChartClick = new EventEmitter<unknown>();

	public isControllerVisible: boolean = false;
	public effectiveConfigOverrides: EChartsOption = {};
	public popupLabel: string | null = null;
	public popupPosition = { x: 0, y: 0 };
	public readonly isMapReady = signal(false);
	public readonly isMapLoading = signal(true);
	public readonly hasMapError = signal(false);
	private _worldMapService = inject(WorldMapService);
	private _destroyRef = inject(DestroyRef);
	private _chartInstance?: ECharts;
	private _zrClickHandler?: (event: { target?: unknown }) => void;
	private _defaultMapConfig: EChartsOption = DEFAULT_CHART_OPTIONS.map();

	public currentZoom: number = (this._defaultMapConfig.series as MapSeriesOption)?.zoom || 3;
	public minZoom: number =
		(this._defaultMapConfig.series as MapSeriesOption)?.scaleLimit?.min || 2;
	public maxZoom: number =
		(this._defaultMapConfig.series as MapSeriesOption)?.scaleLimit?.max || 6;

	private _currentPosition: (number | string)[] = (
		this._defaultMapConfig.series as MapSeriesOption
	)?.center || [0, 45];

	ngOnInit() {
		this._loadMap();
	}

	ngOnDestroy() {
		this._detachOutsideClickHandler();
	}

	public refresh() {
		if (this.hasMapError()) {
			this._loadMap();
		} else {
			this.handleMapRefresh.emit();
		}
	}

	private _loadMap() {
		this.isMapLoading.set(true);
		this.hasMapError.set(false);
		this._worldMapService
			.load()
			.pipe(takeUntilDestroyed(this._destroyRef))
			.subscribe({
				next: () => {
					this.isMapReady.set(true);
					this.isMapLoading.set(false);
				},
				error: () => {
					this.hasMapError.set(true);
					this.isMapLoading.set(false);
				}
			});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes["data"]) {
			this.data = changes["data"].currentValue;
		}

		if (changes["data"] || changes["configOverrides"] || changes["showPointPopupOnClick"]) {
			this._syncEffectiveConfigOverrides();
		}
	}

	/**
	 * Increases the current zoom level by one, up to the maximum zoom level.
	 * If the current zoom level is already at the maximum, the method returns without making any changes.
	 * Hides the controller and updates the configuration overrides with the new zoom level.
	 */
	public zoomIn() {
		if (this.currentZoom === this.maxZoom) return;

		this.isControllerVisible = false;
		this.currentZoom++;

		this.configOverrides = {
			...this.configOverrides,
			series: {
				zoom: this.currentZoom
			}
		};
		this._syncEffectiveConfigOverrides();
	}

	/**
	 * Decreases the current zoom level by one, if it is not already at the minimum zoom level.
	 * Hides the controller and updates the configuration overrides with the new zoom level.
	 *
	 * @remarks
	 * This method will not decrease the zoom level below the minimum zoom level.
	 */
	public zoomOut() {
		if (this.currentZoom === this.minZoom) return;

		this.isControllerVisible = false;
		this.currentZoom--;

		this.configOverrides = {
			...this.configOverrides,
			series: {
				zoom: this.currentZoom
			}
		};
		this._syncEffectiveConfigOverrides();
	}

	/**
	 * Moves the map to a new position based on the given direction.
	 *
	 * @param position - The direction to move the map. Can be "up", "down", "left", or "right".
	 *
	 * The movement is calculated based on the current zoom level and a fixed step size.
	 * The new position is then updated in the `configOverrides` to reflect the change.
	 */
	public move(position: "up" | "down" | "left" | "right") {
		const [x, y] = this._currentPosition as [number, number];
		const step = 30 * (this.currentZoom / 3);

		switch (position) {
			case "up":
				this._currentPosition = [x, y + step];
				break;
			case "down":
				this._currentPosition = [x, y - step];
				break;
			case "left":
				this._currentPosition = [x - step, y];
				break;
			case "right":
				this._currentPosition = [x + step, y];
				break;
		}

		this.configOverrides = {
			...this.configOverrides,
			series: {
				center: this._currentPosition
			}
		};
		this._syncEffectiveConfigOverrides();
	}

	public onChartInit(echartsInstance: ECharts) {
		this._detachOutsideClickHandler();
		this._chartInstance = echartsInstance;
		this._attachOutsideClickHandler();
		this.handleChartInit.emit(echartsInstance);
	}

	public onChartClick(params: unknown) {
		if (!this.showPointPopupOnClick || !this._chartInstance) {
			this.handleChartClick.emit(params);
			return;
		}

		const clickParams =
			typeof params === "object" && params !== null
				? (params as {
						componentType?: string;
						dataIndex?: number;
						seriesIndex?: number;
						name?: string;
						event?: {
							event?: {
								offsetX?: number;
								offsetY?: number;
							};
						};
					})
				: {};

		if (clickParams.componentType === "markPoint") {
			this.popupLabel = clickParams.name || null;
			this.popupPosition = {
				x: clickParams.event?.event?.offsetX ?? 0,
				y: clickParams.event?.event?.offsetY ?? 0
			};
		} else {
			this.closePopup();
		}

		this.handleChartClick.emit(params);
	}

	public closePopup() {
		this.popupLabel = null;
	}

	private _attachOutsideClickHandler() {
		if (!this.showPointPopupOnClick || !this._chartInstance) return;

		this._zrClickHandler = (event: { target?: unknown }) => {
			if (!event.target) {
				this.closePopup();
			}
		};

		this._chartInstance.getZr().on("click", this._zrClickHandler);
	}

	private _detachOutsideClickHandler() {
		if (!this._chartInstance || !this._zrClickHandler) return;

		const zr = this._chartInstance.getZr?.();

		if (!zr) {
			this._zrClickHandler = undefined;
			return;
		}

		zr.off("click", this._zrClickHandler);
		this._zrClickHandler = undefined;
	}

	private _syncEffectiveConfigOverrides() {
		this.effectiveConfigOverrides = this.showPointPopupOnClick
			? {
					tooltip: {
						triggerOn: "none"
					},
					...this.configOverrides
				}
			: this.configOverrides;
	}
}
